import { useEffect, useState } from "react";
import { isIos, isAndroid, isStandalone } from "shared/utils/platform";
import { dismissInstallPrompt } from "features/account/services/accountService";

const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 dias
const MIN_DISTINCT_VISIT_DAYS = 2;

const dismissedKey = (userId) => `install_prompt_dismissed_at_${userId}`;
const visitsKey = (userId) => `install_prompt_visits_${userId}`;
const lastVisitDayKey = (userId) => `install_prompt_last_visit_day_${userId}`;

const todayKey = () => new Date().toISOString().slice(0, 10);

// Conta dias distintos de uso (não aparições de sessão) — evita mostrar o
// prompt logo na primeira vez que a aluna abre o app.
function trackVisit(userId) {
    const today = todayKey();
    const lastDay = localStorage.getItem(lastVisitDayKey(userId));
    const currentVisits = parseInt(localStorage.getItem(visitsKey(userId)) || "0", 10);

    if (lastDay === today) return currentVisits;

    const visits = currentVisits + 1;
    localStorage.setItem(visitsKey(userId), visits.toString());
    localStorage.setItem(lastVisitDayKey(userId), today);

    return visits;
}

function parseTime(value) {
    if (!value) return null;

    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

// Considera tanto o dismiss local (imediato, mesma sessão/navegador) quanto o
// persistido no backend (via claim do token). A claim só é atualizada no próximo
// login (o token não é reemitido em sessões já abertas), então um dismiss feito
// num aparelho pode levar até a expiração do token (24h) para valer em outro
// aparelho já logado — aceitável dado o baixo impacto do caso de borda.
function isDismissedRecently(userId, claimDismissedAt) {
    const localTime = parseTime(localStorage.getItem(dismissedKey(userId)));
    const claimTime = parseTime(claimDismissedAt);
    const dismissedTime = Math.max(localTime ?? -Infinity, claimTime ?? -Infinity);

    if (!Number.isFinite(dismissedTime)) return false;

    return Date.now() - dismissedTime < DISMISS_COOLDOWN_MS;
}

export function useInstallPrompt(userId, installPromptDismissedAt) {
    const [show, setShow] = useState(false);
    const platform = isIos() ? "ios" : isAndroid() ? "android" : null;

    useEffect(() => {
        if (!userId || !platform || isStandalone()) {
            setShow(false);
            return;
        }

        if (isDismissedRecently(userId, installPromptDismissedAt)) {
            setShow(false);
            return;
        }

        const visits = trackVisit(userId);
        setShow(visits >= MIN_DISTINCT_VISIT_DAYS);
    }, [userId, platform, installPromptDismissedAt]);

    function dismiss() {
        if (userId) localStorage.setItem(dismissedKey(userId), new Date().toISOString());
        // Best-effort: o dismiss local já esconde o banner nesse aparelho, então
        // uma falha aqui não deve travar a UI — mas também não pode ficar muda.
        dismissInstallPrompt().catch((error) => console.error(error));
        setShow(false);
    }

    return { show, platform, dismiss };
}
